<script setup>
import {computed, inject, ref} from "vue";
import {useDsoInformationStore} from "@/stores/dsoCompanyInformationStore"; // Make sure to create this store
import {useI18n} from 'vue-i18n';
import {useFrontEndStore} from "@/stores/frontEndStore";
import router from "@/router/routerSetup";
import { DialogConfirm, PrimaryButton, SecondaryButton } from '@es-bill/es-bill-common-components';

const $log = inject('log');
const {t} = useI18n();
const dsoInformationStore = useDsoInformationStore();
const frontEndStore = useFrontEndStore();

const sortBy = [{key: 'name', order: 'asc'}];

const tableHeader = computed(() => [
  {title: t('name'), value: 'name'},
  {title: 'ECN', value: 'ecn'},
  {title: t('zip'), value: 'zips'},
  {title: t('topology'), value: 'linkCheckNetTopology'},
  {title: t('portal'), value: 'linkCustomerPortal'},
  {title: t('digitalContract'), value: 'supportsDigitalContract'},
  {title: t('actions'), value: 'actions', sortable: false},
]);

dsoInformationStore.fetchDsoInformation();

const operatorsAvailable = computed(() => dsoInformationStore.informations.map(c => ({...c, props: {subtitle: c.ecn }})));

const operatorList = computed(() => {
  let infos = dsoInformationStore.informations;

  // Apply dso filter
  if (frontEndStore.dsoFilter.ecnList.length)
    infos = infos.filter(info => frontEndStore.dsoFilter.ecnList.includes(info.ecn));

  return infos;
});

function openLink(url) {
  if (url) {
    $log.info('Opening link', url);
    window.open(url, '_blank');
  }
}

const isDialogVisible = ref(false);
const selectedEcn = ref(null); // To store the selected ECN for deletion

function showDeleteDialog(ecn) {
  selectedEcn.value = ecn;
  isDialogVisible.value = true;
}

function handleConfirm() {
  if (selectedEcn.value) {
    dsoInformationStore.deleteDsoInformation(selectedEcn.value);
    selectedEcn.value = null; // Reset the selected ECN
  }
  isDialogVisible.value = false;
}

function handleCancel() {
  selectedEcn.value = null; // Reset the selected ECN
  isDialogVisible.value = false;
}

</script>

<template>
  <v-container class="pa-0">
    <div class="d-flex align-center mb-4">
      <p class="textHeadline flex-grow-1">{{ $t('dsoCompanies') }}</p>
    </div>

    <div class="d-flex flex-column flex-md-row w-100 mb-3">
        <v-autocomplete
          class="w-100"
          bg-color="white"
          :label="$t('filter') + ' ' + $t('dsos')"
          v-model="frontEndStore.dsoFilter.ecnList"
          :items="operatorsAvailable"
          :filter-keys="['title','props.subtitle']"
          multiple
          variant="outlined"
          item-value="ecn"
          item-title="name"
          clearable
        ></v-autocomplete>
        <div class="d-flex flex-column flex-md-row w-100 ga-md-3">
          <PrimaryButton prepend-icon="mdi-plus" :text="t('createDsoCompany')" @click="router.push({ name: 'dsoCompanyForm' })"/>
          <SecondaryButton :text="t('replaceDsoCompanies')" @click="router.push({ name: 'replaceDsoCompanies' })"/>
        </div>
      </div>



      <v-data-table
        class="elevation-2 mainViewContent"
        :sort-by="sortBy"
        :headers="tableHeader"
        :items="operatorList">

        <!-- Custom slots for link columns -->
        <template v-slot:item.zips="{ item }">
          <span>{{ item.zips.join(', ') }}</span>
        </template>

        <template v-slot:item.linkCheckNetTopology="{ item }">
          <v-icon
            :disabled="!item.linkCheckNetTopology"
            @click="openLink(item.linkCheckNetTopology, '_blank')">
            mdi-open-in-new
          </v-icon>
        </template>

        <template v-slot:item.linkCustomerPortal="{ item }">
          <v-icon
            :disabled="!item.linkCustomerPortal"
            @click="openLink(item.linkCustomerPortal)">
            mdi-open-in-new
          </v-icon>
        </template>

        <template v-slot:item.supportsDigitalContract="{ item }">
          <v-icon v-if="item.supportsDigitalContract" color="success">mdi-check</v-icon>
        </template>

        <!-- Actions column -->
        <template v-slot:item.actions="{ item }">
          <v-icon
             @click="showDeleteDialog(item.ecn)"
            class="mr-2"
          >mdi-delete
          </v-icon>
          <v-icon
            @click="router.push({ name: 'dsoCompanyForm', params: { id: item.ecn }})"
          >mdi-pencil
          </v-icon>
        </template>
      </v-data-table>

     <!-- Include the DialogConfirm common component -->
    <DialogConfirm
      :modelValue="isDialogVisible"
      @update:modelValue="isDialogVisible = $event"
      @confirm="handleCancel"
      @cancel="handleConfirm"
      :title="t('areYouSure')"
      :message="t('deleteConfirmationGeneric')"
      :confirmText="t('cancel')"
      :cancelText="t('confirm')"
    />
  </v-container>

</template>

<style scoped>
</style>
